import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { format } from 'date-fns';
import { BehaviorSubject, Subscription } from 'rxjs';
import { randomId } from 'server/shared/helpers/random';
import { trimPhone } from 'server/shared/helpers/trimPhoneHelper';
import { CarLicenseEnum } from 'server/shared/models/carLicense.enum';
import { Certificate } from 'server/shared/models/certificate';
import {
  ExternalInviteeInput,
  Invitee,
  InviteeFileReference,
} from 'server/shared/models/invitee';
import { InviteeFile } from 'server/shared/models/inviteeFile';
import {
  ExternalInvitee,
  ExternalTransferInfo,
} from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { LanguageEnum } from 'server/shared/models/language.enum';
import { SexEnum } from 'server/shared/models/sex.enum';
import { Speciality } from 'server/shared/models/speciality';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
import { getJobInviteeStatusAndIcon } from 'src/app/shared/getInviteeStatusAndIcon';
import { downloadFile } from '../../../helpers/downloadFile';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import { ConfirmationDialog } from '../../shared/confirmation-dialog/confirmation.component';

@Component({
  selector: 'add-invitee-form',
  templateUrl: 'add-invitee.component.html',
  styleUrls: ['add-invitee.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddInviteeComponent implements OnInit {
  i18n = this.translationService.getTranslations('jobs');
  jobId?: string;
  secret?: string;
  jobStatusEnum = JobStatusEnum;
  inviteeStatusEnum = InviteeStatusEnum;
  isLoadingJob$ = new BehaviorSubject<boolean>(true);

  sexEnum = SexEnum;
  languageEnum = LanguageEnum;
  carLicenseEnum = CarLicenseEnum;
  note: any;

  subscriptions: Subscription = new Subscription();

  externalTransferInfo$ = new BehaviorSubject<ExternalTransferInfo | undefined>(
    undefined,
  );
  form: FormGroup = new FormGroup({});
  showForm$ = new BehaviorSubject<boolean>(false);
  canNotDeclineJob$ = new BehaviorSubject<boolean>(false);

  files$ = new BehaviorSubject<InviteeFile[]>([]);
  filesForInvitee$ = new BehaviorSubject<InviteeFileReference[]>([]);
  isUploading$ = new BehaviorSubject<boolean>(false);
  isDownloading$ = new BehaviorSubject<boolean>(false);
  progressbarValue$ = new BehaviorSubject<number>(0);

  dateOfBirth$ = new BehaviorSubject<string | undefined>(undefined);

  // Getters for backward compatibility
  get isLoadingJob() {
    return this.isLoadingJob$.value;
  }
  get externalTransferInfo() {
    return this.externalTransferInfo$.value;
  }
  get showForm() {
    return this.showForm$.value;
  }
  get canNotDeclineJob() {
    return this.canNotDeclineJob$.value;
  }
  get files() {
    return this.files$.value;
  }
  get filesForInvitee() {
    return this.filesForInvitee$.value;
  }
  get isUploading() {
    return this.isUploading$.value;
  }
  get isDownloading() {
    return this.isDownloading$.value;
  }
  get progressbarValue() {
    return this.progressbarValue$.value;
  }
  get dateOfBirth() {
    return this.dateOfBirth$.value;
  }

  @ViewChild('autosize') autosize?: CdkTextareaAutosize;
  @ViewChild('submitEmployeeSection')
  submitEmployeeSection?: ElementRef<HTMLElement>;

  constructor(
    private router: Router,
    private toastService: ToastService,
    private ngZone: NgZone,
    private route: ActivatedRoute,
    private translationService: TranslationService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.jobId = this.route.snapshot.paramMap.get('jobId') || '';
    this.secret = this.route.snapshot.paramMap.get('secret') || '';

    this.getJobInfo();

    this.form = new FormGroup(
      {
        sex: new FormControl(null, [Validators.required]),
        firstName: new FormControl(null, [Validators.required]),
        lastName: new FormControl(null, [Validators.required]),
        email: new FormControl(null, [Validators.required, Validators.email]),
        phone: new FormControl(null, [Validators.required]),
        pricePerHour: new FormControl(null, [Validators.min(0)]),
        additionalInfo: new FormControl(null, []),
        certificates: new FormControl(null, []),
        specialities: new FormControl(null, []),
      },
      { updateOn: 'change' },
    );
  }

  getJobInfo() {
    Meteor.call(
      'getExternalTransferInfo',
      this.jobId,
      this.secret,
      (error: Meteor.Error, result: any) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: 'jobNotFound',
          });
        } else {
          this.ngZone.run(() => {
            this.externalTransferInfo$.next(result);
            this.canNotDeclineJob$.next(
              this.externalTransferInfo$.value?.invitees.some(
                (invitee) => invitee.status === InviteeStatusEnum.BOOKED,
              ) ||
                this.externalTransferInfo$.value?.declined ||
                false,
            );
            this.files$.next([]);
            this.filesForInvitee$.next([]);
          });
        }
        this.ngZone.run(() => {
          this.isLoadingJob$.next(false);
        });
      },
    );
  }

  getInviteeIcon(invitee: Invitee) {
    return getJobInviteeStatusAndIcon(invitee).stateIcon;
  }

  getIconClass(invitee: Invitee) {
    if (invitee.status === InviteeStatusEnum.BOOKED) {
      return 'book';
    } else if (
      invitee.status === InviteeStatusEnum.CANCELED ||
      invitee.status === InviteeStatusEnum.UNAVAILABLE ||
      invitee.status === InviteeStatusEnum.DECLINED
    ) {
      return 'cancel';
    } else {
      return 'available';
    }
  }

  transferInviteesExcludingPreviouslyPossible(
    info: ExternalTransferInfo | undefined,
  ): ExternalInvitee[] {
    if (!info?.invitees?.length) {
      return [];
    }
    return info.invitees.filter(
      (invitee) =>
        invitee.status !== InviteeStatusEnum.PREVIOUSLY_POSSIBLE_CANDIDATE,
    );
  }

  previouslyPossibleTransferInvitees(
    info: ExternalTransferInfo | undefined,
  ): ExternalInvitee[] {
    if (!info?.invitees?.length) {
      return [];
    }
    return info.invitees.filter(
      (invitee) =>
        invitee.status === InviteeStatusEnum.PREVIOUSLY_POSSIBLE_CANDIDATE,
    );
  }

  startResubmitFromPreviouslyPossible(invitee: ExternalInvitee): void {
    if (!invitee?.personalData || !this.form) {
      return;
    }
    this.ngZone.run(() => {
      this.form!.patchValue({
        sex: invitee.personalData.sex ?? null,
        firstName: invitee.personalData.firstName,
        lastName: invitee.personalData.lastName,
        email: invitee.personalData.email,
        phone: invitee.personalData.phone ?? '',
        pricePerHour: invitee.personalData.pricePerHour ?? null,
        additionalInfo: invitee.additionalInfo ?? null,
        certificates: invitee.personalData.certificates ?? null,
        specialities: invitee.personalData.specialities ?? null,
      });
      this.dateOfBirth$.next(invitee.personalData.dateOfBirth);
      this.files$.next([]);
      this.filesForInvitee$.next([]);
      this.showForm$.next(true);
      this.cdr.markForCheck();
      setTimeout(() => {
        this.submitEmployeeSection?.nativeElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 150);
    });
  }

  declineTransfer() {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: 'declineTransfer',
        text: this.i18n?.confirmTransferReject || 'Dienst wirklich ablehnen?',
      },
    });
    dialogRef.afterClosed().subscribe((res: any) => {
      if (res) {
        Meteor.call(
          'declineTransferExternal',
          this.jobId,
          this.secret,
          (error: Meteor.Error) => {
            if (error) {
              this.toastService.add({
                type: 'ERROR',
                message: error.error?.toString(),
              });
              return;
            }
            this.toastService.add({
              type: 'SUCCESS',
              message: 'transferDeclined',
            });
            this.canNotDeclineJob$.next(true);
            if (!this.externalTransferInfo$.value) return;
            const updatedInfo = {
              ...this.externalTransferInfo$.value,
              declined: true,
            };
            this.externalTransferInfo$.next(updatedInfo);
          },
        );
      }
    });
  }

  submit() {
    if (
      this.form?.invalid ||
      (this.externalTransferInfo?.enableFiles && !this.files$.value.length) ||
      (this.externalTransferInfo?.requiredFields?.certificates &&
        !this.form?.controls?.certificates?.value?.length) ||
      (this.externalTransferInfo?.requiredFields?.sex &&
        (this.form?.controls?.sex?.value === undefined ||
          this.form?.controls?.sex?.value === null)) ||
      (this.externalTransferInfo?.requiredFields?.dateOfBirth &&
        !this.dateOfBirth$.value)
    ) {
      this.form.markAllAsTouched();
      return;
    }
    let invitee: ExternalInviteeInput = {
      sex: this.form?.controls.sex.value,
      email: this.form?.controls.email.value,
      firstName: this.form?.controls.firstName.value,
      lastName: this.form?.controls.lastName.value,
      phone: this.form?.controls.phone.value,
      pricePerHour: this.form?.controls.pricePerHour.value,
      additionalInfo: this.form?.controls.additionalInfo.value,
      certificates: this.form?.controls.certificates.value,
      specialities: this.form?.controls.specialities.value,
      inviteeFiles: this.filesForInvitee$.value,
      dateOfBirth: this.dateOfBirth$.value,
    };

    // @ts-ignore
    Meteor.apply('addInviteeToJob', [invitee, this.jobId, this.secret], {
      wait: false,
      onResultReceived: (
        error: Meteor.Error | Error | undefined,
        result?: { success: boolean; isPossibleCandidate: boolean },
      ) => {
        if (error && 'error' in error) {
          this.toastService.add({
            type: 'ERROR',
            message: (error as Meteor.Error).error?.toString(),
          });
          return;
        }
        this.getJobInfo();
        const message = result?.isPossibleCandidate
          ? 'userSubmittedAsPossibleCandidate'
          : 'userBookedSuccessfullyJob';
        this.toastService.add({
          type: 'SUCCESS',
          message,
        });
        this.form?.reset();
        if (!this.form) return;
        Object.keys(this.form.controls).forEach((key) => {
          this.form?.controls[key].setErrors(null);
        });
      },
    });
  }

  trimPhone() {
    if (this.form?.controls.phone?.value) {
      this.form.controls.phone.setValue(
        trimPhone(this.form.controls.phone.value),
      );
    }
  }

  certificateSelectionChanged(certificates: Certificate[]) {
    const inviteeCertificates = certificates.map((certificate) => {
      return { id: certificate._id, displayName: certificate.name };
    });
    this.form?.controls.certificates.setValue(inviteeCertificates);
  }

  specialitySelectionChanged(specialities: Speciality[]) {
    const inviteeSpecialities = specialities.map((speciality) => {
      return { id: speciality._id, displayName: speciality.name };
    });
    this.form?.controls.specialities.setValue(inviteeSpecialities);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  uploadFile(event: any) {
    this.isUploading$.next(true);
    const files = (event.target as HTMLInputElement).files;
    // 15MB
    const maxFileSize = 1024 * 1024 * 15;
    if (!FileReader || !files || !files.length) {
      this.toastService.add({ type: 'ERROR', message: 'notSupported' });
      return;
    } else {
      Array.from(files).forEach((uploadFile) => {
        const file = uploadFile as File;
        if (file.size < maxFileSize && this.externalTransferInfo?.ownTransfer) {
          const fileReader = new FileReader();
          fileReader.onload = () => {
            if (
              !fileReader.result ||
              !this.externalTransferInfo$.value?.ownTransfer
            ) {
              this.isUploading$.next(false);
              this.toastService.add({
                type: 'ERROR',
                message: 'fileUploadFailed',
              });
              return;
            }

            const fileId = randomId();

            const inviteeFile: InviteeFile = {
              _id: fileId,
              customerId:
                this.externalTransferInfo$.value!.ownTransfer!.targetCustomer
                  .id,
              fileName: file.name,
              createdAt: Date.now(),
              updatedAt: Date.now(),
              path: fileId,
              size: file.size.toString(),
              type: file.type,
              blobString: fileReader.result?.toString(),
            };

            Meteor.call(
              'upsertInviteeFile',
              inviteeFile,
              (error: Meteor.Error, result: InviteeFile) => {
                if (error) {
                  this.isUploading$.next(false);
                  this.toastService.add({
                    type: 'ERROR',
                    message: 'serverError',
                  });
                }
                if (result) {
                  this.isUploading$.next(false);

                  result.type = result.fileName.split('.').pop() || '';
                  this.files$.next([...this.files$.value, result]);
                  this.filesForInvitee$.next([
                    ...this.filesForInvitee$.value,
                    {
                      id: result._id,
                      fileName: result.fileName,
                      path: result.path,
                      size: result.size,
                      type: result.type,
                    },
                  ]);
                  this.toastService.add({
                    type: 'SUCCESS',
                    message: 'documentUploaded',
                  });
                }
              },
            );
          };
          fileReader.readAsDataURL(file);
        } else {
          this.isUploading$.next(false);
          this.toastService.add({
            type: 'ERROR',
            message: 'maxFileUploadExceeded',
          });
        }
      });
    }
  }

  removeFileDialog(file: InviteeFile, templateRef: any) {
    const dialogRef = this.dialog.open(templateRef);
    dialogRef.afterClosed().subscribe((response) => {
      if (response) {
        this.removeFile(file);
      }
    });
  }

  removeFile(inviteeFile: InviteeFile) {
    Meteor.call(
      'removeInviteeFile',
      inviteeFile,
      (error: Meteor.Error, result: InviteeFile) => {
        if (error) {
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        if (result) {
          this.filesForInvitee$.next(
            this.filesForInvitee$.value.filter((file) => {
              return file.id !== result._id;
            }),
          );
          this.files$.next(
            this.files$.value.filter((file) => {
              return file._id !== result._id;
            }),
          );
          this.toastService.add({
            type: 'SUCCESS',
            message: 'removedSuccessfully',
          });
        }
      },
    );
  }

  downloadInviteeFile(event: Event, file: InviteeFile) {
    this.isDownloading$.next(true);
    downloadFile(file, '', (percentage: number) => {
      this.progressbarValue$.next(percentage);
    })
      .then(() => {
        this.isDownloading$.next(false);
      })
      .catch(() => {
        this.toastService.add({
          type: 'ERROR',
          message: 'fileNotFound',
        });
        this.isDownloading$.next(false);
      });
  }

  selectDateOfBirth(event: MatDatepickerInputEvent<any & string>) {
    this.dateOfBirth$.next(format(event.value, dbDayDateToken));
  }

  clearDateOfBirth(): void {
    this.dateOfBirth$.next(undefined);
  }
}
